import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Archive,
  Customer,
  DeviceType,
  Extinguisher,
  Hose,
  HoseConfig,
  Inspection,
  InspectionDraft,
  InspectionReading,
  Manufacturer,
  Model,
  ServicePrice,
  Settings,
  SparePart,
  Visit,
  Worker,
} from '@/data/types';
import { VisitStatus } from '@/data/types';
import { deriveCustomerStatus } from '@/lib/customer-status';
import { onSiteExtinguishers, HOSE_COUNT_KEY } from '@/lib/device-counts';
import { normalizeDeviceTypeKey } from '@/lib/device-type-label';
import { Lang } from '@/lib/lang';
import { getDecoded, onChange as onAuthChange } from '@/lib/auth';
import {
  put as adapterPut,
  removeByTypeId as adapterRemove,
  removeSparePartByArticleNr,
  putSparePartByArticleNr,
  docIdPart,
} from '@/lib/store-adapter';
import {
  runShadow,
  pendingCount,
  subscribe as subscribeSyncQueue,
  setSyncErrorHandler,
  startSyncQueue,
} from '@/lib/sync-queue';
import {
  setReplicationErrorHandler,
  setOnlineHandler,
  setSyncActivityHandler,
  SyncActivity,
} from '@/lib/couch';
import { canWrite } from '@/lib/permissions';
import {
  can,
  customerWriteChecks,
  hoseConfigWriteChecks,
  PermAction,
  SectionId,
  type SectionCheck,
} from '@/lib/section-permissions';
import { t } from '@/lib/i18n';

// All domain mutators now shadow-write to PouchDB via the store-adapter so the
// per-tenant CouchDB receives the change (see W1 #8). The zustand persist blob
// still backs the UI for offline reads until the read-path is migrated.

interface CurrentUser {
  name: string;
  email: string;
  tenantId: string;
  roles: string[];
}

function deriveUserFromJwt(): CurrentUser | null {
  const d = getDecoded();
  if (!d) return null;
  return {
    name: d.name ?? d.email ?? d.sub,
    email: d.email ?? d.sub ?? '',
    tenantId: d.tenant_id ?? '',
    roles: d.roles ?? [],
  };
}

function denied(what: string, roles: string[]): false {
  const { lang, toast } = useApp.getState();
  toast(t('perm.denied', lang));
  console.warn('[perm] blocked', what, 'for roles', roles);
  return false;
}

// Block a write the current user may not make, before it touches the store or
// the local DB. Two checks, both must pass:
//   1. the server floor — the doc types must pass canWrite, mirroring the
//      server's validate_doc_update. PouchDB's local validator runs with empty
//      roles so it won't enforce this offline; without the client guard a
//      forbidden write lands in the local DB and only bounces on push (silent
//      divergence).
//   2. the section rights from Verwaltung → Berechtigungen (client #95), which
//      the server does not know about, so this is where they are enforced.
// The UI already hides what a role may not do; this is defense in depth at the
// persistence boundary. Returns false and toasts when blocked.
//
// The legacy settings.permMatrix is no longer passed to canWrite: it is folded
// into the section rights (lib/section-permissions.ts), so it keeps tightening
// until an admin saves the new matrix and stops once they have.
function guardWrite(
  types: string | readonly string[],
  checks: readonly SectionCheck[] = []
): boolean {
  const roles = deriveUserFromJwt()?.roles ?? [];
  for (const type of typeof types === 'string' ? [types] : types) {
    if (!canWrite(roles, type)) return denied(`write to type ${type}`, roles);
  }
  const settings = useApp.getState().settings;
  for (const [section, action] of checks) {
    if (!can(roles, settings, section, action)) {
      return denied(`${action} on ${section}`, roles);
    }
  }
  return true;
}

/** Create when the record is new, edit when it already exists. */
const upsertCheck = (section: SectionId, exists: boolean): SectionCheck => [
  section,
  exists ? PermAction.EDIT : PermAction.CREATE,
];

/**
 * Guard a customer-doc write. Without explicit `checks` the rights are worked
 * out from what changed (customerWriteChecks): customer fields, and devices
 * added / changed / removed in the embedded extinguisher and hose lists.
 * Maintenance flows pass their own checks, because stamping service dates on
 * devices is what carrying out a maintenance means, not a device edit.
 */
function guardCustomer(
  prev: Customer | undefined,
  next: Customer | undefined,
  checks?: readonly SectionCheck[]
): boolean {
  return guardWrite('customer', checks ?? customerWriteChecks(prev, next));
}

/**
 * Apply one change to an existing customer: build the next version, guard it
 * against the previous one, then update the store and persist. A blocked or
 * unknown customer changes nothing.
 */
function commitCustomer(
  id: string,
  build: (prev: Customer) => Customer,
  checks?: readonly SectionCheck[]
): void {
  const prev = useApp.getState().customers.find(c => c.id === id);
  if (!prev) return;
  const next = build(prev);
  if (!guardCustomer(prev, next, checks)) return;
  useApp.setState({
    customers: useApp.getState().customers.map(c => (c.id === id ? next : c)),
  });
  shadowPutCustomer(next);
}

// Inspection scheduling. Each device type may define its own service interval
// (years); fall back to the global settings.inspectionInterval when unset.
function addYears(isoDate: string, years: number): string {
  const d = new Date(isoDate);
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
}
function typeInterval(
  typeId: string | undefined,
  deviceTypes: DeviceType[],
  fallback: number
): number {
  return (
    deviceTypes.find(dt => dt.id === typeId)?.inspectionInterval ?? fallback
  );
}
function earliestDate(dates: (string | undefined)[]): string | undefined {
  return dates.filter((d): d is string => Boolean(d)).sort()[0];
}

// Write-through to PouchDB via the durable sync queue: on failure the write is
// retried automatically and the first failure is surfaced to the user, so a
// transient error never silently drops a record.
//
// The shadow helpers do not guard: every mutator calls guardWrite /
// guardCustomer first, before it changes the in-memory state, so a blocked
// write leaves both the screen and the local DB untouched.
function shadowPutCustomer(c: Customer): void {
  const idPart = docIdPart('customer', c.id);
  const doc = {
    ...(c as unknown as Record<string, unknown>),
    _id: `customer:${idPart}`,
    type: 'customer',
  };
  runShadow(`put:customer:${idPart}`, () => adapterPut(doc as never));
}

/**
 * Generic shadow-put for non-customer domain entities. The caller supplies the
 * doc `type` and the id segment used in `_id`.
 */
function shadowPut(type: string, idPart: string, doc: unknown): void {
  const part = docIdPart(type, idPart);
  const next = {
    ...(doc as Record<string, unknown>),
    _id: `${type}:${part}`,
    type,
  };
  runShadow(`put:${type}:${part}`, () => adapterPut(next as never));
}

/**
 * Write-through delete to PouchDB so the CouchDB delete replicates. Queued and
 * retried on failure like the puts.
 */
function shadowRemove(type: string, idPart: string): void {
  const part = docIdPart(type, idPart);
  runShadow(`remove:${type}:${part}`, () => adapterRemove(type, part));
}

// Spare parts resolve their persisted doc by articleNr (legacy docs are keyed
// spare_part:legacy-N, not by articleNr), so route their writes/deletes through
// dedicated adapter helpers instead of the generic id-part shadow functions.
function shadowPutSparePart(fields: {
  articleNr: string;
  [k: string]: unknown;
}): void {
  runShadow(`put:spare_part:${fields.articleNr}`, () =>
    putSparePartByArticleNr(fields)
  );
}
function shadowRemoveSparePart(articleNr: string): void {
  runShadow(`remove:spare_part:${articleNr}`, () =>
    removeSparePartByArticleNr(articleNr)
  );
}

export enum FilterStatus {
  ALL = 'all',
  DONE = 'done',
  WAITING = 'waiting',
  TODO = 'todo',
  NONE = 'none',
}

export enum SortDir {
  ASC = 'asc',
  DESC = 'desc',
}

interface AppState {
  online: boolean;
  unsyncedCount: number;
  // What replication is doing right now, surfaced in the sidebar (#68).
  syncActivity: SyncActivity;
  // First-sync progress (session-derived, never persisted): remote doc_count
  // once known, local docs replicated so far, and whether the initial cold
  // download has finished.
  syncTarget: number | null;
  syncLoaded: number;
  firstSyncComplete: boolean;
  customers: Customer[];
  selectedCustomerIds: Set<string>;
  manufacturers: Manufacturer[];
  deviceTypes: DeviceType[];
  spareParts: SparePart[];
  servicePrices: ServicePrice[];
  visits: Visit[];
  inspections: Inspection[];
  // Unsubmitted maintenance work, keyed by visit id, plus the visit whose
  // dialog is open. Both live here rather than in the dialog so the work
  // outlives navigation, dismissal and reload (client #64).
  inspectionDrafts: Record<string, InspectionDraft>;
  activeVisitId: string | null;
  workers: Worker[];
  settings: Settings;
  hoseConfig: HoseConfig;
  toasts: { id: number; msg: string }[];
  search: string;
  filterStatus: FilterStatus;
  filterWorker: string;
  filterCity: string;
  sortCol: string;
  sortDir: SortDir;
  tourSeen: boolean;
  tourActive: boolean;
  tourStep: number;
  lang: Lang;
  sidebarOpen: boolean;
  hydrated: boolean;
  currentUser: CurrentUser | null;
  activeTenantId: string | null;
  setHydrated: (v: boolean) => void;
  // Drop every backend-owned slice. Replication merges never delete (see
  // store-merge), so a switch to another login or tenant must start empty or
  // the previous one's customers stay on screen.
  resetDomain: () => void;
  setCurrentUser: (u: CurrentUser | null) => void;
  setActiveTenantId: (id: string | null) => void;
  setLang: (l: Lang) => void;
  setOnline: (v: boolean) => void;
  setSyncActivity: (a: SyncActivity) => void;
  setSyncTarget: (v: number | null) => void;
  setSyncLoaded: (v: number) => void;
  setFirstSyncComplete: (v: boolean) => void;
  setSidebarOpen: (v: boolean) => void;
  toggleSidebar: () => void;
  toggleSelected: (id: string) => void;
  selectAll: (v: boolean) => void;
  clearFilters: () => void;
  setFilter: (
    k: 'search' | 'filterStatus' | 'filterWorker' | 'filterCity',
    v: string
  ) => void;
  setSort: (col: string) => void;
  toast: (msg: string) => void;
  dismissToast: (id: number) => void;
  updateCustomer: (id: string, patch: Partial<Customer>) => void;
  addCustomer: (c: Customer) => void;
  deleteCustomer: (id: string) => void;
  upsertExtinguisher: (customerId: string, ext: Extinguisher) => void;
  deleteExtinguisher: (customerId: string, extId: string) => void;
  // The "maintained this cycle" tick — a maintenance right, not a device edit.
  setMaintained: (customerId: string, extId: string, value: boolean) => void;
  batchUpdateInspection: (customerId: string) => void;
  upsertHose: (customerId: string, hose: Hose) => void;
  deleteHose: (customerId: string, hoseId: string) => void;
  createArchive: (customerId: string) => void;
  updateSettings: (patch: Partial<Settings>) => void;
  updateHoseConfig: (patch: Partial<HoseConfig>) => void;
  upsertManufacturer: (m: Manufacturer) => void;
  deleteManufacturer: (id: string) => void;
  upsertModel: (mfrId: string, model: Model) => void;
  deleteModel: (mfrId: string, modelId: string) => void;
  upsertDeviceType: (t: DeviceType) => void;
  deleteDeviceType: (id: string) => void;
  upsertSparePart: (p: SparePart) => void;
  renameSparePart: (oldArticleNr: string, next: SparePart) => void;
  deleteSparePart: (articleNr: string) => void;
  upsertServicePrice: (p: ServicePrice) => void;
  deleteServicePrice: (articleNr: string) => void;
  // `as` names the section the write belongs to: a calendar booking (default)
  // or the visit a walk-in maintenance creates (SectionId.MAINTENANCE).
  upsertVisit: (v: Visit, as?: SectionId) => void;
  deleteVisit: (id: string) => void;
  assignVisit: (visitId: string, techEmail: string) => void;
  setActiveVisit: (visitId: string | null) => void;
  saveInspectionDraft: (visitId: string, draft: InspectionDraft) => void;
  clearInspectionDraft: (visitId: string) => void;
  completeVisit: (
    visitId: string,
    args: {
      devicesServiced: string[];
      readings?: InspectionReading[];
      technician: string;
      date: string;
      signatureKey?: string;
      notes?: string;
    }
    // false when the visit is unknown or the write was not allowed.
  ) => Promise<boolean>;
  upsertWorker: (w: Worker) => void;
  deleteWorker: (id: string) => void;
  startTour: () => void;
  nextTourStep: () => void;
  prevTourStep: () => void;
  endTour: () => void;
  setTourStep: (n: number) => void;
}

let toastId = 0;

const computeDeviceCounts = (
  c: Customer,
  deviceTypes: DeviceType[] = []
): Record<string, number> => {
  const counts: Record<string, number> = {};
  // Disposed units stay in the array as a service record but are not on site,
  // so they must not be counted (client #77).
  onSiteExtinguishers(c.extinguishers).forEach(e => {
    if (!e.type) return;
    // Key on the canonical device-type id so label-shaped legacy values and
    // id-shaped app values collapse into one bucket (client #80).
    const key = normalizeDeviceTypeKey(e.type, deviceTypes);
    counts[key] = (counts[key] ?? 0) + 1;
  });
  if (c.hoses.length) counts[HOSE_COUNT_KEY] = c.hoses.length;
  return counts;
};

// Re-derive the cached fields (device counts + auto status) after any change
// to a customer's devices or inspection dates.
const recompute = (c: Customer, deviceTypes: DeviceType[] = []): Customer => ({
  ...c,
  deviceCounts: computeDeviceCounts(c, deviceTypes),
  status: deriveCustomerStatus(c),
});

// Backend-only data: initial state is empty. PouchDB + tenant seedCatalog
// (apps/api/internal/handlers/tenants.go) is the sole source of truth.
// initStoreFromPouchDB (lib/init-store.ts) hydrates this after auth.
const EMPTY_SETTINGS: Settings = {
  country: '',
  companyName: '',
  vatNumber: '',
  street: '',
  zip: '',
  city: '',
  phone: '',
  email: '',
  vatPercent: 0,
  currency: '',
  rounding: '',
  paymentTerms: 0,
  inspectionInterval: 0,
  boostAgeLimit: 0,
  permanentAgeLimit: 0,
  hoseTestInterval: 0,
  travelSurcharge: 0,
  iban: '',
};
const EMPTY_HOSE_CONFIG: HoseConfig = {
  hoseDiameters: [],
  connectionDiameters: [],
  hoseModels: [],
};

export const useApp = create<AppState>()(
  persist(
    (set, get) => ({
      online: true,
      unsyncedCount: 0,
      syncActivity: SyncActivity.IDLE,
      syncTarget: null,
      syncLoaded: 0,
      firstSyncComplete: false,
      customers: [],
      selectedCustomerIds: new Set<string>(),
      manufacturers: [],
      deviceTypes: [],
      spareParts: [],
      servicePrices: [],
      visits: [],
      inspections: [],
      inspectionDrafts: {},
      activeVisitId: null,
      workers: [],
      settings: EMPTY_SETTINGS,
      hoseConfig: EMPTY_HOSE_CONFIG,
      toasts: [],
      search: '',
      filterStatus: FilterStatus.ALL,
      filterWorker: '',
      filterCity: '',
      sortCol: 'name',
      sortDir: SortDir.ASC,
      tourSeen: false,
      tourActive: false,
      tourStep: 0,
      lang: Lang.EN,
      sidebarOpen: false,
      hydrated: false,
      currentUser: deriveUserFromJwt(),
      activeTenantId: deriveUserFromJwt()?.tenantId ?? null,
      setHydrated: v => set({ hydrated: v }),
      resetDomain: () =>
        set({
          customers: [],
          selectedCustomerIds: new Set<string>(),
          manufacturers: [],
          deviceTypes: [],
          spareParts: [],
          servicePrices: [],
          visits: [],
          inspections: [],
          inspectionDrafts: {},
          activeVisitId: null,
          workers: [],
          settings: EMPTY_SETTINGS,
          hoseConfig: EMPTY_HOSE_CONFIG,
        }),
      setCurrentUser: u =>
        set({ currentUser: u, activeTenantId: u?.tenantId ?? null }),
      setActiveTenantId: id => set({ activeTenantId: id }),
      setLang: l => set({ lang: l }),
      setOnline: v => set({ online: v }),
      setSyncActivity: a => set({ syncActivity: a }),
      setSyncTarget: v => set({ syncTarget: v }),
      setSyncLoaded: v => set({ syncLoaded: v }),
      setFirstSyncComplete: v => set({ firstSyncComplete: v }),
      setSidebarOpen: v => set({ sidebarOpen: v }),
      toggleSidebar: () => set({ sidebarOpen: !get().sidebarOpen }),
      toggleSelected: id => {
        const next = new Set(get().selectedCustomerIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        set({ selectedCustomerIds: next });
      },
      selectAll: v => {
        if (v)
          set({ selectedCustomerIds: new Set(get().customers.map(c => c.id)) });
        else set({ selectedCustomerIds: new Set() });
      },
      clearFilters: () =>
        set({
          search: '',
          filterStatus: FilterStatus.ALL,
          filterWorker: '',
          filterCity: '',
        }),
      setFilter: (k, v) => set({ [k]: v } as Partial<AppState>),
      setSort: col => {
        const cur = get();
        if (cur.sortCol === col)
          set({
            sortDir: cur.sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC,
          });
        else set({ sortCol: col, sortDir: SortDir.ASC });
      },
      toast: msg => {
        const id = ++toastId;
        set({ toasts: [...get().toasts, { id, msg }] });
        setTimeout(() => get().dismissToast(id), 3000);
      },
      dismissToast: id =>
        set({ toasts: get().toasts.filter(t => t.id !== id) }),
      // Every customer write re-derives the cached status/deviceCounts. Without
      // this, paths that patch devices through updateCustomer (replace, copy)
      // left the cached fields stale, and a customer nobody edited kept whatever
      // the Excel import stamped — which is why almost every record sat at
      // status "none" despite having a due date (client #79).
      updateCustomer: (id, patch) =>
        commitCustomer(id, c =>
          recompute({ ...c, ...patch }, get().deviceTypes)
        ),
      addCustomer: c => {
        // Who created it, so a technician keeps a customer they added even
        // before it has an appointment (server scope, client #92).
        const createdBy = c.createdBy ?? deriveUserFromJwt()?.email;
        const created = recompute({ ...c, createdBy }, get().deviceTypes);
        if (!guardCustomer(undefined, created)) return;
        set({ customers: [...get().customers, created] });
        shadowPutCustomer(created);
      },
      deleteCustomer: id => {
        const prev = get().customers.find(c => c.id === id);
        if (!guardCustomer(prev, undefined)) return;
        set({ customers: get().customers.filter(c => c.id !== id) });
        shadowRemove('customer', id);
      },
      upsertExtinguisher: (customerId, ext) =>
        commitCustomer(customerId, c => {
          const exists = c.extinguishers.some(e => e.id === ext.id);
          const extinguishers = exists
            ? c.extinguishers.map(e => (e.id === ext.id ? ext : e))
            : [...c.extinguishers, ext];
          return recompute({ ...c, extinguishers }, get().deviceTypes);
        }),
      deleteExtinguisher: (customerId, extId) =>
        commitCustomer(customerId, c =>
          recompute(
            {
              ...c,
              extinguishers: c.extinguishers.filter(e => e.id !== extId),
            },
            get().deviceTypes
          )
        ),
      setMaintained: (customerId, extId, value) =>
        commitCustomer(
          customerId,
          c =>
            recompute(
              {
                ...c,
                extinguishers: c.extinguishers.map(e =>
                  e.id === extId ? { ...e, maintainedThisCycle: value } : e
                ),
              },
              get().deviceTypes
            ),
          [[SectionId.MAINTENANCE, PermAction.EDIT]]
        ),
      batchUpdateInspection: customerId => {
        const { settings, deviceTypes } = get();
        const today = new Date().toISOString().slice(0, 10);
        const fallback = settings.inspectionInterval;
        commitCustomer(
          customerId,
          c => {
            const extinguishers = c.extinguishers.map(e => ({
              ...e,
              lastInspection: today,
              nextInspection: addYears(
                today,
                typeInterval(e.type, deviceTypes, fallback)
              ),
              maintainedThisCycle: true,
            }));
            // The customer-level reminder uses the soonest-due device date.
            const custNext =
              earliestDate(extinguishers.map(e => e.nextInspection)) ??
              addYears(today, fallback);
            return recompute(
              {
                ...c,
                lastInspection: today,
                nextInspection: custNext,
                extinguishers,
              },
              get().deviceTypes
            );
          },
          // "Inspect all" stamps service dates without an inspection record —
          // a maintenance edit, not a device edit.
          [[SectionId.MAINTENANCE, PermAction.EDIT]]
        );
      },
      upsertHose: (customerId, hose) =>
        commitCustomer(customerId, c => {
          const exists = c.hoses.some(h => h.id === hose.id);
          const hoses = exists
            ? c.hoses.map(h => (h.id === hose.id ? hose : h))
            : [...c.hoses, hose];
          return recompute({ ...c, hoses }, get().deviceTypes);
        }),
      deleteHose: (customerId, hoseId) =>
        commitCustomer(customerId, c =>
          recompute(
            { ...c, hoses: c.hoses.filter(h => h.id !== hoseId) },
            get().deviceTypes
          )
        ),
      createArchive: customerId => {
        commitCustomer(customerId, c => {
          // Snapshot the register as it stands, not just its size: an archive
          // is the record of what was on site at that date, and the client
          // wants to open one and see every device with its dates and the
          // spare parts used (client #73). Without this the drill-down has
          // nothing to show.
          const archive: Archive = {
            id: `A-${Date.now()}`,
            date: new Date().toISOString().slice(0, 10),
            deviceCount: c.extinguishers.length + c.hoses.length,
            technician: c.worker ?? 'Unknown',
            devices: [
              ...c.extinguishers.map(e => ({
                deviceNumber: e.deviceNumber,
                floor: e.floor,
                location: e.location,
                mfr: e.mfr,
                model: e.model,
                content: e.content,
                type: e.type,
                year: e.year,
                lastInspection: e.lastInspection,
                nextInspection: e.nextInspection,
                refilled: e.refilled,
                spareParts: e.spareParts,
              })),
              ...c.hoses.map(h => ({
                deviceNumber: h.deviceNumber,
                floor: h.floor,
                location: h.location,
                mfr: h.mfr,
                model: h.model,
                year: h.year,
                lastInspection: h.lastService,
                nextInspection: h.nextService,
                spareParts: h.spareParts,
              })),
            ],
          };
          return { ...c, archives: [archive, ...c.archives] };
        });
      },
      updateSettings: patch => {
        if (!guardWrite('settings', [[SectionId.SETTINGS, PermAction.EDIT]])) {
          return;
        }
        const next = { ...get().settings, ...patch };
        set({ settings: next });
        // settings is a singleton — stable id keeps replication idempotent
        shadowPut('settings', 'settings', next);
      },
      updateHoseConfig: patch => {
        const next = { ...get().hoseConfig, ...patch };
        if (
          !guardWrite(
            'hose_config',
            hoseConfigWriteChecks(get().hoseConfig, next)
          )
        ) {
          return;
        }
        set({ hoseConfig: next });
        // hoseConfig is a singleton — stable id keeps replication idempotent
        shadowPut('hose_config', 'hose_config', next);
      },
      upsertManufacturer: m => {
        const cur = get().manufacturers;
        const exists = cur.some(x => x.id === m.id);
        if (
          !guardWrite('manufacturer', [
            upsertCheck(SectionId.MANUFACTURERS, exists),
          ])
        ) {
          return;
        }
        const next = exists
          ? cur.map(x => (x.id === m.id ? m : x))
          : [...cur, m];
        set({ manufacturers: next });
        shadowPut('manufacturer', m.id, m);
      },
      deleteManufacturer: id => {
        if (
          !guardWrite('manufacturer', [
            [SectionId.MANUFACTURERS, PermAction.DELETE],
          ])
        ) {
          return;
        }
        set({ manufacturers: get().manufacturers.filter(m => m.id !== id) });
        shadowRemove('manufacturer', id);
      },
      // Models live inside the manufacturer and are managed on the same page,
      // so they share the Hersteller rights: a new model is a create, a
      // changed one an edit, a removed one a delete.
      upsertModel: (mfrId, model) => {
        const exists = get()
          .manufacturers.find(m => m.id === mfrId)
          ?.models.some(x => x.id === model.id);
        if (
          !guardWrite('manufacturer', [
            upsertCheck(SectionId.MANUFACTURERS, !!exists),
          ])
        ) {
          return;
        }
        const next = get().manufacturers.map(m => {
          if (m.id !== mfrId) return m;
          const exists = m.models.some(x => x.id === model.id);
          return {
            ...m,
            models: exists
              ? m.models.map(x => (x.id === model.id ? model : x))
              : [...m.models, model],
          };
        });
        set({ manufacturers: next });
        // Models live nested on the manufacturer doc — replicate the parent
        const updated = next.find(m => m.id === mfrId);
        if (updated) shadowPut('manufacturer', updated.id, updated);
      },
      deleteModel: (mfrId, modelId) => {
        if (
          !guardWrite('manufacturer', [
            [SectionId.MANUFACTURERS, PermAction.DELETE],
          ])
        ) {
          return;
        }
        const next = get().manufacturers.map(m =>
          m.id === mfrId
            ? { ...m, models: m.models.filter(x => x.id !== modelId) }
            : m
        );
        set({ manufacturers: next });
        const updated = next.find(m => m.id === mfrId);
        if (updated) shadowPut('manufacturer', updated.id, updated);
      },
      upsertDeviceType: t => {
        const cur = get().deviceTypes;
        const exists = cur.some(x => x.id === t.id);
        if (
          !guardWrite('device_type', [
            upsertCheck(SectionId.DEVICE_TYPES, exists),
          ])
        ) {
          return;
        }
        const next = exists
          ? cur.map(x => (x.id === t.id ? t : x))
          : [...cur, t];
        set({ deviceTypes: next });
        shadowPut('device_type', t.id, t);
      },
      deleteDeviceType: id => {
        if (
          !guardWrite('device_type', [
            [SectionId.DEVICE_TYPES, PermAction.DELETE],
          ])
        ) {
          return;
        }
        set({ deviceTypes: get().deviceTypes.filter(t => t.id !== id) });
        shadowRemove('device_type', id);
      },
      upsertSparePart: p => {
        const cur = get().spareParts;
        const exists = cur.some(x => x.articleNr === p.articleNr);
        if (
          !guardWrite('spare_part', [
            upsertCheck(SectionId.SPARE_PARTS, exists),
          ])
        ) {
          return;
        }
        const next = exists
          ? cur.map(x => (x.articleNr === p.articleNr ? p : x))
          : [...cur, p];
        set({ spareParts: next });
        // spareParts key on articleNr (no `id` field). The domain `type`
        // (extinguisher|hose) collides with the doc discriminator `type`
        // that shadowPut sets, so persist it as `partType` (init-store
        // maps it back on hydration).
        const { type: partType, ...rest } = p;
        shadowPutSparePart({ ...rest, partType });
      },
      renameSparePart: (oldArticleNr, next) => {
        // The article number is the spare-part doc key AND the reference used
        // by extinguishers / hoses / archives / inspection readings. Client
        // decision: "old reports follow rename" — so a rename repoints every
        // reference from old → new instead of leaving a historical snapshot.
        const renamed = oldArticleNr !== next.articleNr;
        // A rename also rewrites the customers and inspections that cite the
        // old number, so all three types have to be writable.
        if (
          !guardWrite(
            renamed ? ['spare_part', 'customer', 'inspection'] : 'spare_part',
            [[SectionId.SPARE_PARTS, PermAction.EDIT]]
          )
        ) {
          return;
        }

        set({
          spareParts: get().spareParts.map(p =>
            p.articleNr === oldArticleNr ? next : p
          ),
        });

        const persistPart = (part: SparePart) => {
          const { type: partType, ...rest } = part;
          shadowPutSparePart({ ...rest, partType });
        };

        if (!renamed) {
          persistPart(next);
          return;
        }

        // Repoint an article-number reference list; returns the SAME array
        // reference when nothing changed so callers can detect a no-op.
        const repoint = (arr?: string[]): string[] | undefined =>
          arr && arr.includes(oldArticleNr)
            ? arr.map(a => (a === oldArticleNr ? next.articleNr : a))
            : arr;

        const changedCustomers: Customer[] = [];
        const nextCustomers = get().customers.map(c => {
          let touched = false;
          const extinguishers = c.extinguishers.map(e => {
            const sp = repoint(e.spareParts);
            if (sp !== e.spareParts) {
              touched = true;
              return { ...e, spareParts: sp };
            }
            return e;
          });
          const hoses = c.hoses.map(h => {
            const sp = repoint(h.spareParts);
            if (sp !== h.spareParts) {
              touched = true;
              return { ...h, spareParts: sp };
            }
            return h;
          });
          const archives = c.archives.map(a => {
            if (!a.devices) return a;
            let aTouched = false;
            const devices = a.devices.map(d => {
              const sp = repoint(d.spareParts);
              if (sp !== d.spareParts) {
                aTouched = true;
                return { ...d, spareParts: sp };
              }
              return d;
            });
            if (!aTouched) return a;
            touched = true;
            return { ...a, devices };
          });
          if (!touched) return c;
          const nc = { ...c, extinguishers, hoses, archives };
          changedCustomers.push(nc);
          return nc;
        });

        const changedInspections: Inspection[] = [];
        const nextInspections = get().inspections.map(ins => {
          if (!ins.readings) return ins;
          let touched = false;
          const readings = ins.readings.map(r => {
            const sp = repoint(r.spareParts);
            if (sp !== r.spareParts) {
              touched = true;
              return { ...r, spareParts: sp };
            }
            return r;
          });
          if (!touched) return ins;
          const ni = { ...ins, readings };
          changedInspections.push(ni);
          return ni;
        });

        set({ customers: nextCustomers, inspections: nextInspections });

        shadowRemoveSparePart(oldArticleNr);
        persistPart(next);
        changedCustomers.forEach(shadowPutCustomer);
        changedInspections.forEach(ins => shadowPut('inspection', ins.id, ins));
      },
      deleteSparePart: articleNr => {
        if (
          !guardWrite('spare_part', [
            [SectionId.SPARE_PARTS, PermAction.DELETE],
          ])
        ) {
          return;
        }
        set({
          spareParts: get().spareParts.filter(p => p.articleNr !== articleNr),
        });
        shadowRemoveSparePart(articleNr);
      },
      upsertServicePrice: p => {
        const cur = get().servicePrices;
        const exists = cur.some(x => x.articleNr === p.articleNr);
        if (
          !guardWrite('service_price', [
            upsertCheck(SectionId.SERVICE_PRICES, exists),
          ])
        ) {
          return;
        }
        const next = exists
          ? cur.map(x => (x.articleNr === p.articleNr ? p : x))
          : [...cur, p];
        set({ servicePrices: next });
        // servicePrices key on articleNr (no `id` field)
        shadowPut('service_price', p.articleNr, p);
      },
      deleteServicePrice: articleNr => {
        if (
          !guardWrite('service_price', [
            [SectionId.SERVICE_PRICES, PermAction.DELETE],
          ])
        ) {
          return;
        }
        set({
          servicePrices: get().servicePrices.filter(
            p => p.articleNr !== articleNr
          ),
        });
        shadowRemove('service_price', articleNr);
      },
      upsertVisit: (v, as = SectionId.CALENDAR) => {
        const cur = get().visits;
        const exists = cur.some(x => x.id === v.id);
        // A walk-in maintenance books its own visit; that is carrying out a
        // maintenance, not planning one in the calendar.
        const check: SectionCheck =
          as === SectionId.MAINTENANCE
            ? [SectionId.MAINTENANCE, PermAction.CREATE]
            : upsertCheck(SectionId.CALENDAR, exists);
        if (!guardWrite('visit', [check])) return;
        const next = exists
          ? cur.map(x => (x.id === v.id ? v : x))
          : [...cur, v];
        set({ visits: next });
        shadowPut('visit', v.id, v);
      },
      deleteVisit: id => {
        if (!guardWrite('visit', [[SectionId.CALENDAR, PermAction.DELETE]])) {
          return;
        }
        set({ visits: get().visits.filter(v => v.id !== id) });
        get().clearInspectionDraft(id);
        shadowRemove('visit', id);
      },
      setActiveVisit: visitId => set({ activeVisitId: visitId }),
      saveInspectionDraft: (visitId, draft) =>
        set({
          inspectionDrafts: { ...get().inspectionDrafts, [visitId]: draft },
        }),
      clearInspectionDraft: visitId => {
        const { [visitId]: _gone, ...rest } = get().inspectionDrafts;
        set({ inspectionDrafts: rest });
      },
      assignVisit: (visitId, techEmail) => {
        if (!guardWrite('visit', [[SectionId.CALENDAR, PermAction.EDIT]])) {
          return;
        }
        // Keep the Mitarbeiter name in step with the login (client #92).
        const w = get().workers.find(x => x.email === techEmail);
        const next = get().visits.map(v =>
          v.id === visitId
            ? { ...v, assignedTechId: techEmail, worker: w?.name ?? v.worker }
            : v
        );
        const v = next.find(x => x.id === visitId);
        set({ visits: next });
        if (v) shadowPut('visit', v.id, v);
      },
      completeVisit: async (visitId, args) => {
        const v = get().visits.find(x => x.id === visitId);
        if (!v) return false;
        // Completing writes the inspection, the visit and the customer's
        // device dates in one go — all of it is carrying out the maintenance.
        if (
          !guardWrite(
            ['inspection', 'visit', 'customer'],
            [[SectionId.MAINTENANCE, PermAction.CREATE]]
          )
        ) {
          return false;
        }
        const id = `insp-${crypto.randomUUID()}`;
        const insp: Inspection = {
          id,
          visitId,
          customerId: v.customerId,
          technician: args.technician,
          date: args.date,
          devicesServiced: args.devicesServiced,
          readings: args.readings,
          signatureKey: args.signatureKey,
          notes: args.notes,
        };
        const completed: Visit = {
          ...v,
          status: VisitStatus.COMPLETED,
          completedAt: new Date().toISOString(),
        };
        const { settings, deviceTypes, workers } = get();
        const fallback = settings.inspectionInterval || 0;
        // The customer's worker should name whoever actually serviced the site
        // — the tech assigned in the calendar, else the one signing off. Both
        // are emails; the Metadata card shows a name (client #71).
        // Falling back to `v.worker` here meant the metadata never moved:
        // startMaintenance pre-fills the visit's worker from the customer's
        // existing technician, so an unrecognised signer resolved straight back
        // to the stale name (client #71). The signer wins; their worker record
        // only supplies a display name for the email.
        const techEmail = v.assignedTechId || args.technician;
        const servicedBy =
          workers.find(w => w.email === techEmail)?.name ?? techEmail;
        set({
          inspections: [...get().inspections, insp],
          visits: get().visits.map(x => (x.id === visitId ? completed : x)),
          customers: get().customers.map(c => {
            if (c.id !== v.customerId) return c;
            const readingFor = (deviceId: string) =>
              (args.readings ?? []).find(r => r.deviceId === deviceId);
            const extinguishers = c.extinguishers.map(e => {
              const er = readingFor(e.id);
              // Only devices actually serviced this visit get stamped. Devices
              // left unticked (billing "no entry") keep their previous dates,
              // so an inspection can no longer claim work it did not do — the
              // hose branch below has always worked this way (client #67).
              if (!er || er.kind !== 'extinguisher') return e;
              return {
                ...e,
                refilled: er.refilled || e.refilled,
                // Parts fitted during this visit are recorded on the
                // inspection, which is what the rapport and the invoice read.
                // Leaving them on the device made the next maintenance open
                // pre-populated with the last visit's parts, and they rode
                // along on every copy (client #87, #86).
                spareParts: [],
                photos: er.photos ?? e.photos,
                billing: er.billing ?? e.billing,
                maintainedThisCycle: true,
                lastInspection: args.date,
                nextInspection: addYears(
                  args.date,
                  typeInterval(e.type, deviceTypes, fallback)
                ),
              };
            });
            // Apply the per-visit hose readings onto the device record so the
            // hose's current state + Hose Service Report reflect this visit.
            // Hoses follow the annual SN EN 671-3 control cycle.
            const hoses = c.hoses.map(h => {
              const r = readingFor(h.id);
              if (!r || r.kind !== 'hose') return h;
              return {
                ...h,
                restingPressure: r.restingPressure ?? h.restingPressure,
                flowPressure: r.flowPressure ?? h.flowPressure,
                flowRate: r.flowRate ?? h.flowRate,
                condition: r.condition ?? h.condition,
                conditionNotes: r.conditionNotes ?? h.conditionNotes,
                conditionReplaced: r.conditionReplaced ?? h.conditionReplaced,
                // Hoses are classified and billed like extinguishers now
                // (client #91).
                billing: r.billing ?? h.billing,
                // Parts belong to the visit, not the device — see the
                // extinguisher branch above (client #87).
                spareParts: [],
                photos: r.photos ?? h.photos,
                lastService: args.date,
                nextService: addYears(args.date, 1),
              };
            });
            const custNext =
              earliestDate([
                ...extinguishers.map(e => e.nextInspection),
                ...hoses.map(h => h.nextService),
              ]) ?? addYears(args.date, fallback);
            return recompute(
              {
                ...c,
                worker: servicedBy || c.worker,
                lastInspection: args.date,
                nextInspection: custNext,
                extinguishers,
                hoses,
              },
              get().deviceTypes
            );
          }),
        });
        // The work is now on the inspection — the draft has served its purpose.
        get().clearInspectionDraft(visitId);
        if (get().activeVisitId === visitId) set({ activeVisitId: null });
        shadowPut('inspection', id, insp);
        shadowPut('visit', visitId, completed);
        const cust = get().customers.find(c => c.id === v.customerId);
        if (cust) shadowPut('customer', cust.id, cust);
        return true;
      },
      upsertWorker: w => {
        const cur = get().workers;
        const exists = cur.some(x => x.id === w.id);
        if (!guardWrite('worker', [upsertCheck(SectionId.WORKERS, exists)])) {
          return;
        }
        const next = exists
          ? cur.map(x => (x.id === w.id ? w : x))
          : [...cur, w];
        set({ workers: next });
        shadowPut('worker', w.id, w);
      },
      deleteWorker: id => {
        if (!guardWrite('worker', [[SectionId.WORKERS, PermAction.DELETE]])) {
          return;
        }
        set({ workers: get().workers.filter(w => w.id !== id) });
        shadowRemove('worker', id);
      },
      startTour: () => set({ tourActive: true, tourStep: 0 }),
      nextTourStep: () => set({ tourStep: get().tourStep + 1 }),
      prevTourStep: () => set({ tourStep: Math.max(0, get().tourStep - 1) }),
      endTour: () => set({ tourActive: false, tourSeen: true, tourStep: 0 }),
      setTourStep: n => set({ tourStep: n }),
    }),
    {
      // v4: domain data is NO LONGER persisted to localStorage. The backend
      // (CouchDB → PouchDB `ft-local` IndexedDB) is the sole source of truth;
      // the store hydrates from PouchDB on auth (see components/AuthGate.tsx).
      // Only UI preferences live here so they survive reload without caching
      // any backend-owned data locally.
      name: 'fire-track-app-v4',
      version: 4,
      partialize: state => ({
        tourSeen: state.tourSeen,
        lang: state.lang,
        // Unsubmitted field work, not backend-owned data: it has no home on the
        // server until the visit is completed, so localStorage is what keeps a
        // half-finished maintenance alive across a reload.
        inspectionDrafts: state.inspectionDrafts,
        activeVisitId: state.activeVisitId,
      }),
    }
  )
);

// Purge pre-v4 persisted blobs that cached backend-owned domain data in
// localStorage. v4 only persists UI prefs; these old keys are never read again.
if (typeof window !== 'undefined') {
  ['fire-track-app-v3', 'fire-track-app-v2', 'fire-track-app'].forEach(k => {
    try {
      window.localStorage.removeItem(k);
    } catch {
      // ignore — storage may be unavailable
    }
  });
}

// keep currentUser/activeTenantId in sync with auth state
onAuthChange(() => {
  const user = deriveUserFromJwt();
  useApp.setState({
    currentUser: user,
    activeTenantId: user?.tenantId ?? null,
  });
});

// Surface PouchDB write-through failures instead of silently dropping records.
// First failure toasts; the unsynced count drives any UI indicator; the queue
// retries automatically (timer / online / tab focus).
setSyncErrorHandler(() => {
  const { lang } = useApp.getState();
  useApp.getState().toast(t('sync.saveFailed', lang));
});
subscribeSyncQueue(() => {
  useApp.setState({ unsyncedCount: pendingCount() });
});
startSyncQueue();

// Surface server-side replication rejects (a doc denied by validate_doc_update
// on push — e.g. a role-forbidden write that slipped past the local guard, or a
// conflict). Without this the denial is swallowed and the record silently never
// reaches the server.
setReplicationErrorHandler(() => {
  const { lang } = useApp.getState();
  useApp.getState().toast(t('sync.rejected', lang));
});

// Drive the online flag from real connectivity instead of a manual toggle:
// browser online/offline events plus replication active / paused-with-error.
setOnlineHandler(v => useApp.getState().setOnline(v));
setSyncActivityHandler(a => useApp.getState().setSyncActivity(a));
if (typeof window !== 'undefined') {
  const sync = (v: boolean) => useApp.getState().setOnline(v);
  sync(navigator.onLine);
  window.addEventListener('online', () => sync(true));
  window.addEventListener('offline', () => sync(false));
}
